import type { ReactNode } from "react";
import { flushSync } from "react-dom";
import { createRoot, type Root } from "react-dom/client";
import { ApiError } from "./modules/api-client.js";
import { setUserSpeech } from "./modules/app-config.js";
import { getCurrentUser } from "./modules/auth.js";
import { t } from "./modules/i18n.js";
import { captureError, setSentryUser } from "./modules/sentry.js";
import { startSessionWatch } from "./modules/session-watch.js";
import { loadStylesheet } from "./modules/theme.js";
import { Toaster } from "./simple/components/sc-toaster";
import type { AppUser } from "./simple/app/app-user";

/**
 * **Der Start der Seite** — welche Oberfläche gezeichnet wird, und in welcher
 * Reihenfolge. main.tsx ruft `boot()`; hier steht es für sich, damit ein Test
 * jeden Weg einzeln gehen kann (boot.test.tsx).
 *
 * **Eine Wurzel für alles** (`<sc-app>`): Anmeldeseite, Passwort-Link,
 * Oberfläche und die Boot-Fehlerkarte zeichnen in dieselbe, dazu der Toaster
 * am `<body>`. Gezeichnet wird synchron (`flushSync`), denn gleich danach geht
 * das Ladebild weg — dazwischen stünde sonst eine leere Fläche.
 *
 * **Die Bündel bleiben getrennt**: Anmeldung und Oberfläche kommen über einen
 * dynamischen Import, und wer nicht angemeldet ist, lädt keine Zeile der
 * Schale (CLAUDE.md, „The login page is its own chunk").
 */
let root: Root | null = null;

function paint(app: Element, view: ReactNode): void {
  const r = root ?? createRoot(app);
  root = r;
  flushSync(() => r.render(<>{view}<Toaster /></>));
}

export async function boot(): Promise<void> {
  const app = document.querySelector("sc-app");
  if (!app) { console.error("[sc] <sc-app> missing"); return; }

  if (window.location.pathname === "/platform-login") { await platformLogin(app); return; }
  // Der Link aus Einladung und „Passwort vergessen" (docs/11 9): Er hängt an
  // keiner Anmeldung und gehört ins kleine Bündel der Anmeldeseite.
  if (window.location.pathname === "/set-password") {
    try {
      const { setPasswordScreen } = await import("./simple/login");
      paint(app, await setPasswordScreen());
    } catch (err) {
      renderBootError(app, t("Failed to load the interface. Please reload the page."), err);
    }
    hideBootScreen();
    return;
  }

  // **Die Zustimmungsseite** (docs/08 7aa) hängt an keiner Anmeldung: Wer sie
  // öffnet, hat eine E-Mail bekommen und soll eine Freigabe geben, oft vom
  // Telefon. Wie `/set-password` darüber also vor dem Benutzer — und mit
  // eigenem kleinen Bündel.
  if (window.location.pathname.startsWith("/consent/")) {
    try {
      const { consentScreen } = await import("./simple/consent");
      paint(app, await consentScreen(window.location.pathname.slice("/consent/".length)));
    } catch (err) {
      renderBootError(app, t("Failed to load the interface. Please reload the page."), err);
    }
    hideBootScreen();
    return;
  }

  let user: AppUser | null = null;
  try { user = await waitForUser(); } catch (err) {
    renderBootError(app, t("Backend connection failed: {message}", { message: err instanceof Error ? err.message : String(err) }), err);
    return;
  }
  setUserSpeech(user);

  // Die Anmeldung hängt an keinem Benutzer und hat ihr eigenes kleines Bündel —
  // wer nicht angemeldet ist, lädt die Oberfläche dahinter nicht mit.
  if (!user) {
    try {
      const css = loadStylesheet("/assets/simple.css");
      const { loginScreen } = await import("./simple/login");
      await css;
      paint(app, await loginScreen());
    } catch (err) {
      renderBootError(app, t("Failed to load the interface. Please reload the page."), err);
    }
    hideBootScreen();
    return;
  }

  setSentryUser(user);
  // **Ab hier ist ein 401 eine Aussage.** Vorher war es die reguläre Auskunft
  // „nicht angemeldet", die eine Zeile darüber zur Anmeldeseite führt; von hier
  // an heißt es, dass eine bestehende Anmeldung weggefallen ist (siehe
  // modules/session-watch.ts).
  startSessionWatch();
  try {
    // Stylesheet und Bündel **nebeneinander** holen, und erst danach zeichnen.
    // Nacheinander wäre es zweimal Warten; ohne das Warten malte die Oberfläche
    // mit base.css allein (siehe loadStylesheet).
    const css = loadStylesheet("/assets/simple.css");
    const { App, afterFirstPaint } = await import("./simple/app/app");
    await css;
    // Wer angemeldet auf /login landet (Lesezeichen, Zurück), ist zu Hause.
    if (window.location.pathname === "/login") window.history.replaceState({}, "", "/");
    paint(app, <App user={user} />);
    afterFirstPaint(user);
  } catch (err) {
    renderBootError(app, t("Failed to load the interface. Please reload the page."), err);
  }
  hideBootScreen();
}

/**
 * **Ein Plattform-Admin kommt herein** (docs/11 15c). Die Plattform öffnet
 * `/platform-login#t=<nachweis>`; der Nachweis steht im Fragment, damit er in
 * keinem Server-Log landet, und geht von hier einmal an den Server, der die
 * Sitzung setzt. Danach wird die Adresse ersetzt — der Nachweis bleibt nicht im
 * Verlauf stehen.
 */
async function platformLogin(app: Element): Promise<void> {
  const token = new URLSearchParams(window.location.hash.slice(1)).get("t") ?? "";
  window.history.replaceState(null, "", "/platform-login");
  try {
    const res = await fetch("/api/auth/platform-login", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
    const body: unknown = await res.json().catch(() => ({}));
    const field = (key: string): unknown => (body && typeof body === "object" ? Reflect.get(body, key) : undefined);
    if (!res.ok) throw new Error(String(field("error") || `HTTP ${res.status}`));
    const redirect = field("redirect");
    window.location.replace(typeof redirect === "string" && redirect.startsWith("/") ? redirect : "/admin");
  } catch (err) {
    renderBootError(app, t("The platform sign-in has expired or is invalid. Please start it again from the platform."), err);
    hideBootScreen();
  }
}

/** Takt, in dem während einer Störung nachgesehen wird. */
const BOOT_RETRY_MS = 3_000;
/** Erst ab dem zweiten Fehlschlag wird die Wartezeit benannt. */
const RETRIES_UNTIL_NOTE = 2;

/**
 * **Ein Neustart ist kein Fehler, sondern eine Wartezeit.**
 *
 * Wer die Seite genau dann aufruft, wenn der Server unten ist, sah früher
 * „Verbindung zum Backend fehlgeschlagen" — eine Sackgasse. Dabei ist die Lage
 * in aller Regel vorübergehend: ein Deployment, ein Neustart, zehn Sekunden.
 * Deshalb bleibt das Ladebild aus `index.html` stehen und die Anfrage
 * wiederholt sich, bis der Server antwortet.
 *
 * **Nur solange der Server weg ist.** Alles, was eine ordentliche Antwort ist —
 * auch 401 (`getCurrentUser` liefert dann `null`) oder ein 4xx —, führt sofort
 * weiter bzw. zur Fehlerkarte. Wiederholt wird ausschließlich bei einem
 * Netzfehler und bei 5xx: Traefik ohne Backend (502/503/504) und der Server
 * mit leerem `/app/public` (500).
 */
async function waitForUser(): Promise<AppUser | null> {
  let attempt = 0;
  for (;;) {
    try {
      const user = (await getCurrentUser()) as AppUser | null;
      setBootNote(null);
      return user;
    } catch (err) {
      if (!isServerDown(err)) throw err;
      attempt += 1;
      if (attempt >= RETRIES_UNTIL_NOTE) setBootNote(t("SoluCoder is starting — this page continues by itself."));
      await new Promise((resolve) => setTimeout(resolve, BOOT_RETRY_MS));
    }
  }
}

function isServerDown(err: unknown): boolean {
  // Kein `ApiError` heißt: `fetch` selbst ist gescheitert — kein Netz, kein
  // Server. Mit Status zählen nur 5xx; 4xx sind Auskünfte, keine Ausfälle.
  if (!(err instanceof ApiError)) return true;
  return Number(err.status) >= 500;
}

/**
 * Die Zeile unter dem Ladebalken. Sie kommt bewusst verzögert: Ein Neustart
 * dauert rund zehn Sekunden, und ein Text, der bei jedem etwas längeren Laden
 * aufblitzt, beunruhigt mehr als er erklärt.
 */
function setBootNote(text: string | null): void {
  const note = document.getElementById("boot-note");
  if (!note) return;
  note.textContent = text ?? "";
  note.hidden = !text;
}

/**
 * Das Ladebild aus index.html wegnehmen — erst, wenn die Oberfläche wirklich
 * steht (auch im Fehlerfall, sonst läge die Meldung darunter). Ausgeblendet
 * statt sofort entfernt (der harte Schnitt wirkt wie ein Ruckeln), entfernt
 * danach trotzdem: Ein durchsichtiger `position: fixed`-Kasten fängt sonst
 * weiter Klicks.
 */
function hideBootScreen(): void {
  const el = document.getElementById("boot");
  if (!el) return;
  el.classList.add("sc-boot--gone");
  setTimeout(() => el.remove(), 250);
}

/**
 * simple.css ist an dieser Stelle nicht sicher geladen — ein Boot-Fehler
 * (Backend nicht erreichbar, Chunk-Ladefehler nach Redeploy, offline,
 * Ad-Blocker) braucht deshalb eine Darstellung, die nur von den
 * `.sc-boot-error*`-Klassen aus base.css abhängt.
 */
function renderBootError(app: Element, message: string, err: unknown): void {
  console.error("[sc] boot failed", err);
  // Ein Boot-Fehler ist der teuerste Fehler überhaupt: Von hier aus gibt es
  // keine Oberfläche mehr. Netzfehler und Deployment-Rauschen filtert
  // `captureError` weg.
  captureError(err, "boot");
  hideBootScreen();
  paint(app, (
    <div className="sc-boot-error">
      <div className="sc-boot-error__card"><p className="sc-boot-error__message">{message}</p></div>
    </div>
  ));
}

/** Nur für Tests: die Wurzel abbauen, damit der nächste Start frisch beginnt. */
export function resetBootForTest(): void {
  root?.unmount();
  root = null;
}
